import { Divider, Flex, H5 } from "orcs-design-system";
import { get } from "lodash";
import { useVersion } from "src/contexts/global/GlobalContext";
import { useTenantId } from "src/contexts/global/ApolloSettingsContext";
import {
  useWorkspace,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { useAuth0 } from "src/contexts/auth0Provider";
import { isInternalSupportUser, auth0UserMetadataKey } from "src/services/auth";
import { isLocalEnvironment } from "src/services/localSettings";
import useUserRole from "src/app/Restricted/useUserRole";
import { filterLinksByLocationAndRole } from "src/util/externalLinks";
import { LINK_LOCATIONS } from "src/consts/externalLinks";
import { useHelpMenuConfig } from "src/hooks/useHelpMenuConfig";
import CustomHelpMenuContent from "./CustomHelpMenuContent";
import DefaultHelpMenuContent from "./DefaultHelpMenuContent";
import SupportInfoFooter from "./SupportInfoFooter";

const parseBackendVersion = (versionString: string | undefined) => {
  if (!versionString) {
    return { commitHash: null, buildNumber: null };
  }
  const match = versionString.match(/^([a-f0-9]+)\s*\[(\d+)\]/);
  if (match) {
    return { commitHash: match[1], buildNumber: match[2] };
  }
  return { commitHash: null, buildNumber: null };
};

const InfoSidebarContent = () => {
  const tenantId = useTenantId();
  const version = useVersion();
  const workspace = useWorkspace();
  const { user } = useAuth0();
  const isSupportUser = isInternalSupportUser(user) || isLocalEnvironment();
  const userRole = useUserRole();
  const { enableCustomHelpAndLinks } = useWorkspaceFeatureFlags();

  const UI_VERSION = process.env["REACT_APP_GIT_SHA"]
    ? process.env["REACT_APP_GIT_SHA"].slice(0, 7)
    : undefined;
  const UI_BUILD = process.env["REACT_APP_BUILD_NUMBER"];
  const workspaceId = get(workspace, "id");
  const workspaceName = get(workspace, "name", "ACTIVE");

  // Get tenant name from user's tenant list
  // Only support users have access to tenantList metadata
  // Directory users and test accounts should NOT have tenantList, so we only access it for support users
  const canAccessTenantList = user && isInternalSupportUser(user);
  const tenantList = canAccessTenantList
    ? get(user, [auth0UserMetadataKey, "tenantList", "tenants"], [])
    : [];
  const matchedTenant = tenantList.find((t) => t.tenantId === tenantId);
  const tenantDisplayName =
    canAccessTenantList && matchedTenant?.name
      ? `${matchedTenant.name} [${tenantId}]`
      : tenantId;

  const { commitHash: beCommitHash, buildNumber: beBuildNumber } =
    parseBackendVersion(version);

  const {
    helpMenuLabel,
    helpMenuHeading,
    helpMenuDescription,
    helpMenuSupportLinkText,
    helpMenuStatusLinkText,
    showTeamFormSupportRequest,
    showTeamFormHelpContent,
    showCustomHelpContent,
    customHelpMenuTitle,
    customHelpMenuDescription,
    maxCustomHelpLinks,
    helpConfig,
  } = useHelpMenuConfig();

  // Get help menu links that are visible to the current user's role
  // externalContents is now inside helpConfig JSON blob
  const externalContents = get(helpConfig, "externalContents");

  // Filter links for custom help section (help and apps locations)
  const customHelpLinks = filterLinksByLocationAndRole(
    externalContents,
    [LINK_LOCATIONS.HELP, LINK_LOCATIONS.APPS],
    userRole
  );

  // Limit custom help links
  const limitedCustomHelpLinks = customHelpLinks.slice(0, maxCustomHelpLinks);

  return (
    <Flex
      flexDirection="column"
      justifyContent="flex-start"
      height="100%"
      gap="r"
    >
      <H5 fontWeight="bold">{helpMenuLabel}</H5>
      <Divider light />

      <CustomHelpMenuContent
        enabled={Boolean(enableCustomHelpAndLinks)}
        showCustomHelpContent={Boolean(showCustomHelpContent)}
        customHelpMenuTitle={customHelpMenuTitle}
        customHelpMenuDescription={customHelpMenuDescription}
        links={limitedCustomHelpLinks}
      />

      <DefaultHelpMenuContent
        show={Boolean(showTeamFormHelpContent)}
        heading={helpMenuHeading}
        description={helpMenuDescription}
        supportLinkText={helpMenuSupportLinkText}
        statusLinkText={helpMenuStatusLinkText}
        showRequestSupport={Boolean(showTeamFormSupportRequest)}
      />

      <SupportInfoFooter
        tenantId={tenantId}
        tenantDisplayName={tenantDisplayName}
        workspaceName={workspaceName}
        workspaceId={workspaceId}
        isSupportUser={isSupportUser}
        uiVersion={UI_VERSION}
        uiBuild={UI_BUILD}
        beVersion={version}
        beCommitHash={beCommitHash}
        beBuildNumber={beBuildNumber}
      />
    </Flex>
  );
};

export default InfoSidebarContent;
